import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import "./SetupReferencePages.css";
import AccountCodeManager from "../managers/AccountCodeManager";
import { AccountCode } from "../interfaces/AccountCodeInterface";
import { useSiteContext } from "../SiteContext";
import config from "../configLoader";
import {
    Hash,
    Search,
    Info,
    ChevronRight,
    ChevronLeft,
    ChevronUp,
    ChevronDown,
    X,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────
type ACSortKey = "code" | "name" | "alt" | "year";
type ACStatus = "active" | "inactive";

const PAGE_SIZES = [15, 30, 50, 100];
const DASH = "—";

// ─── Helpers ─────────────────────────────────────────────────────────────────
function cmp(a: string | number, b: string | number): number {
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function pageWindow(p: number, pages: number): (number | "…")[] {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const out: (number | "…")[] = [1];
    const lo = Math.max(2, p - 1);
    const hi = Math.min(pages - 1, p + 1);
    if (lo > 2) out.push("…");
    for (let i = lo; i <= hi; i++) out.push(i);
    if (hi < pages - 1) out.push("…");
    out.push(pages);
    return out;
}

// ─── Component ───────────────────────────────────────────────────────────────
const AccountCodePage: React.FC = () => {
    const { setLoading, translator } = useSiteContext();

    // Sage workers comp columns — 2020PH only (TAS-160). Other clients' cost codes carry
    // no comp code data, so the columns stay hidden rather than showing three empty ones.
    const showCompCodes = !!config.showWorkersCompCodes;
    const visibleColCount = 6 + (showCompCodes ? 3 : 0);

    // data
    const [data, setData] = useState<AccountCode[] | null>(null);

    // filters / controls
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [status, setStatus] = useState<ACStatus>("active");
    const [sortKey, setSortKey] = useState<ACSortKey>("code");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

    // pagination
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(30);

    // ─── Data loading ─────────────────────────────────────────────────────────
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await AccountCodeManager.getAccountCodes();
            setData(response);
        } catch (err) {
            console.error("Error fetching account codes:", err);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- setLoading unstable from SiteContext
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Reset to page 1 on filter/search/pageSize change (not on sort)
    useEffect(() => { setCurrentPage(1); }, [searchTerm, status, pageSize]);

    // ─── FK lookup map (built once from raw data) ──────────────────────────────
    const codeByID = useMemo(() => {
        const m = new Map<number, AccountCode>();
        (data ?? []).forEach(c => m.set(c.accountCodeID, c));
        return m;
    }, [data]);

    // ─── Filter + sort ────────────────────────────────────────────────────────
    const processed = useMemo(() => {
        if (!data) return [] as AccountCode[];
        // 1. status filter
        let rows = data.filter(c => c.active === (status === "active"));
        // 2. search: pure digits → altAccountCodeID substring; else → name substring
        const q = searchTerm.trim();
        if (q) {
            const digits = /^\d+$/.test(q);
            const lq = q.toLowerCase();
            rows = rows.filter(c =>
                digits
                    // Digit searches also match the comp codes, so payroll can pull
                    // "every cost code on class 5183". (TAS-160)
                    ? String(c.altAccountCodeID).includes(q) ||
                      (showCompCodes &&
                          ((c.compCode1 ?? "").includes(q) || (c.compCode2 ?? "").includes(q)))
                    : c.name.toLowerCase().includes(lq)
            );
        }
        // 3. sort
        const keyFn = (c: AccountCode): string | number => {
            switch (sortKey) {
                case "code": return c.accountCodeID;
                case "name": return c.name.toLowerCase();
                case "alt":  return c.altAccountCodeID;
                case "year": return c.accountingYear || "";
                default:     return c.accountCodeID;
            }
        };
        return [...rows].sort((a, b) => cmp(keyFn(a), keyFn(b)) * (sortDir === "asc" ? 1 : -1));
    }, [data, searchTerm, status, sortKey, sortDir, showCompCodes]);

    // ─── Pagination ───────────────────────────────────────────────────────────
    const total = processed.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const page = Math.min(currentPage, totalPages);
    const startIdx = (page - 1) * pageSize;
    const pageRows = processed.slice(startIdx, startIdx + pageSize);

    const goPage = (n: number) => setCurrentPage(n);

    // ─── Sorting ──────────────────────────────────────────────────────────────
    const handleSort = (key: ACSortKey) => {
        if (sortKey === key) setSortDir(d => (d === "asc" ? "desc" : "asc"));
        else { setSortKey(key); setSortDir("asc"); }
    };

    // ─── Filters ──────────────────────────────────────────────────────────────
    const clearFilters = () => { setSearchTerm(""); setStatus("active"); };

    // ─── FK cell renderer ─────────────────────────────────────────────────────
    const renderLinkCell = (fkId: number): React.ReactNode => {
        if (!fkId) return <span className="dash">{DASH}</span>;
        const linked = codeByID.get(fkId);
        if (!linked) return <span className="sr-mono">{fkId}</span>;
        return (
            <div className="link-cell">
                <span className="lc-id">{linked.altAccountCodeID}</span>
                <span className="lc-name">{linked.name}</span>
            </div>
        );
    };

    // ─── Sortable header cell ─────────────────────────────────────────────────
    const Th: React.FC<{ label: string; sk?: ACSortKey }> = ({ label, sk }) => {
        const sortable = !!sk;
        const sorted = sortable && sortKey === sk;
        const desc = sorted && sortDir === "desc";
        return (
            <th
                className={`${sortable ? "sortable" : ""} ${sorted ? "sorted" : ""}`.trim() || undefined}
                onClick={sortable ? () => handleSort(sk!) : undefined}
            >
                <span className="th-in">
                    {label}
                    {sortable && (desc
                        ? <ChevronDown className="caret" size={13} />
                        : <ChevronUp className="caret" size={13} />
                    )}
                </span>
            </th>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <PageWrapper title={translator.translate("AccountCodePage")} backgroundColor="#f4f5f7">
            <div className="setup-ref">

                {/* breadcrumb */}
                <div className="sr-crumbs">
                    <span>Setup</span>
                    <ChevronRight />
                    <span className="here">Account Codes</span>
                </div>

                {/* header */}
                <div className="sr-app-head">
                    <div className="sr-logo"><Hash size={23} /></div>
                    <div>
                        <h1 className="sr-app-title">Account Codes</h1>
                        <div className="sr-app-sub">
                            A read-only reference of the cost codes used across projects, timesheets, and payroll.
                        </div>
                        <div className="sr-head-note">
                            <Info size={14} />
                            Account codes are managed in the source accounting system.
                        </div>
                        {showCompCodes && (
                            <div className="sr-head-note">
                                <Info size={14} />
                                Comp codes and wage max come from the Sage cost code record and sync
                                nightly. Wage max is an hourly pay rate, not an annual cap: hours paid
                                at or below it use Comp Code 1, hours above it use Comp Code 2.
                            </div>
                        )}
                    </div>
                </div>

                {/* filter bar */}
                <div className="sr-toolbar">
                    <div className="sr-field">
                        <span className="flab">Status</span>
                        <div className="sr-segmented" role="group">
                            <button
                                type="button"
                                className={status === "active" ? "on" : ""}
                                onClick={() => setStatus("active")}
                            >
                                Active
                            </button>
                            <button
                                type="button"
                                className={status === "inactive" ? "on" : ""}
                                onClick={() => setStatus("inactive")}
                            >
                                Inactive
                            </button>
                        </div>
                    </div>
                    <div className="sr-field search">
                        <span className="flab">Search</span>
                        <div className="sr-searchwrap">
                            <Search size={17} className="sr-search-icon" />
                            <input
                                className="sr-input"
                                placeholder="Search by name, or type an ID number"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* result line */}
                <div className="sr-result-line">
                    <span>
                        <b>{total}</b> {total === 1 ? "code" : "codes"}
                    </span>
                    <span className="rl-chip">{status === "active" ? "Active" : "Inactive"}</span>
                    {searchTerm.trim() !== "" && (
                        <span className="rl-chip">
                            "{searchTerm.trim()}"
                            <button
                                type="button"
                                className="rl-x"
                                title="Clear search"
                                onClick={() => setSearchTerm("")}
                            >
                                <X size={13} />
                            </button>
                        </span>
                    )}
                </div>

                {/* table */}
                <div className="sr-card">
                    <div className="sr-tbl-wrap">
                        <table className="sr-dt">
                            <thead>
                                <tr>
                                    <Th label="Name" sk="name" />
                                    <Th label="ID" sk="alt" />
                                    <th>Description</th>
                                    <Th label="Acct. Year" sk="year" />
                                    <th>Overtime Code</th>
                                    <th>Supplement Code</th>
                                    {showCompCodes && <th title="Workers comp class for hours paid at or below the wage max">Comp Code 1</th>}
                                    {showCompCodes && <th title="Hourly pay rate at which the comp code switches from 1 to 2. This is an hourly rate, not an annual cap.">Wage Max</th>}
                                    {showCompCodes && <th title="Workers comp class for hours paid above the wage max">Comp Code 2</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {data !== null && total === 0 && (
                                    <tr>
                                        <td colSpan={visibleColCount}>
                                            <div className="sr-empty">
                                                <div className="eic"><Hash size={26} /></div>
                                                <div className="et">No account codes found</div>
                                                <p>
                                                    {searchTerm.trim() !== ""
                                                        ? `No ${status === "inactive" ? "inactive" : "active"} codes match "${searchTerm.trim()}".`
                                                        : `There are no ${status === "inactive" ? "inactive" : "active"} account codes.`
                                                    }
                                                </p>
                                                {(searchTerm.trim() !== "" || status === "inactive") && (
                                                    <button
                                                        type="button"
                                                        className="sr-btn sm"
                                                        onClick={clearFilters}
                                                    >
                                                        <X size={15} /> Clear filters
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {pageRows.map(code => (
                                    <tr
                                        key={code.accountCodeID}
                                        className={status === "inactive" ? "inactive-row" : ""}
                                    >
                                        {/* Name */}
                                        <td>
                                            <div className="name-cell">
                                                <span className={`nm${status === "inactive" ? " dim" : ""}`}>
                                                    {code.name}
                                                </span>
                                                {status === "inactive" && (
                                                    <span className="sr-pill inactive">
                                                        <span className="dot" />Inactive
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        {/* ID */}
                                        <td>
                                            <span className="sr-mono strong">{code.altAccountCodeID}</span>
                                        </td>
                                        {/* Description */}
                                        <td>
                                            {code.description
                                                ? code.description
                                                : <span className="dash">{DASH}</span>
                                            }
                                        </td>
                                        {/* Acct. Year */}
                                        <td>
                                            {code.accountingYear
                                                ? <span className="sr-mono">{code.accountingYear}</span>
                                                : <span className="dash">{DASH}</span>
                                            }
                                        </td>
                                        {/* Overtime Code */}
                                        <td>{renderLinkCell(code.overtimeAccountCodeID)}</td>
                                        {/* Supplement Code */}
                                        <td>{renderLinkCell(code.supplementAccountCodeID)}</td>
                                        {/* Sage workers comp trio (TAS-160) */}
                                        {showCompCodes && (
                                            <td>
                                                {code.compCode1
                                                    ? <span className="sr-mono">{code.compCode1}</span>
                                                    : <span className="dash">{DASH}</span>
                                                }
                                            </td>
                                        )}
                                        {showCompCodes && (
                                            <td>
                                                {code.wageMax != null
                                                    // "/hr" is load-bearing: this reads like an annual
                                                    // cap and is not one.
                                                    ? <span className="sr-mono">${code.wageMax.toFixed(2)} /hr</span>
                                                    : <span className="dash">{DASH}</span>
                                                }
                                            </td>
                                        )}
                                        {showCompCodes && (
                                            <td>
                                                {code.compCode2
                                                    ? <span className="sr-mono">{code.compCode2}</span>
                                                    : <span className="dash">{DASH}</span>
                                                }
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* pagination — rendered only when total overflows one page */}
                {total > pageSize && (
                    <div className="sr-pager">
                        <span className="count">
                            Showing <b>{startIdx + 1}–{startIdx + pageRows.length}</b> of <b>{total}</b>
                        </span>
                        <span className="psize">
                            Rows
                            <select
                                className="sr-select"
                                value={pageSize}
                                onChange={e => {
                                    setPageSize(parseInt(e.target.value, 10));
                                    setCurrentPage(1);
                                }}
                            >
                                {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </span>
                        {totalPages > 1 && (
                            <span className="pages">
                                <button
                                    className="pg nav"
                                    disabled={page === 1}
                                    onClick={() => goPage(page - 1)}
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                {pageWindow(page, totalPages).map((n, i) =>
                                    n === "…" ? (
                                        <span key={`e${i}`} className="pg ellipsis">…</span>
                                    ) : (
                                        <button
                                            key={n}
                                            className={`pg${n === page ? " on" : ""}`}
                                            onClick={() => goPage(n as number)}
                                        >
                                            {n}
                                        </button>
                                    )
                                )}
                                <button
                                    className="pg nav"
                                    disabled={page === totalPages}
                                    onClick={() => goPage(page + 1)}
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </span>
                        )}
                    </div>
                )}

            </div>
        </PageWrapper>
    );
};

export default AccountCodePage;
